/**
 * Edpies — Student Profile editor.
 *
 * Focused screen that lets a student (or a parent impersonating one) update
 * the bits of `studentProfile` that the Home Dashboard cares about — most
 * importantly **examName** and **examDate** so the Upcoming Exam countdown
 * card can replace its "Set your exam date" placeholder with a real number.
 *
 * Kept deliberately small: a single Card with form fields, a save button
 * that PUTs to /api/student/profile and updates the in-memory `progress`
 * object, then routes back to /welcome. We avoid building a full settings
 * panel here — that lives behind the parent dashboard / admin views.
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ArrowLeft, CalendarDays, Save, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const todayIso = () => new Date().toISOString().slice(0, 10);

const StudentProfilePage = ({ progress, updateProgress }) => {
  const navigate = useNavigate();
  const sp = progress?.user?.studentProfile || {};

  const [examName, setExamName] = useState(sp.examName || '');
  const [examDate, setExamDate] = useState(() => {
    const raw = sp.examDate || '';
    // Convert dd/mm/yyyy → yyyy-mm-dd for the <input type="date"> control.
    if (raw && raw.includes('/')) {
      const [d, m, y] = raw.split('/');
      return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
    }
    return raw.slice(0, 10);
  });
  const [targetSchool, setTargetSchool] = useState(sp.targetSchool || '');
  const [saving, setSaving] = useState(false);

  // Make sure we always have the latest user record so the prefill is fresh
  // even if the user landed here from a deep link.
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => {
        const user = res?.data?.user;
        const next = user?.studentProfile || {};
        setExamName(next.examName || sp.examName || '');
        if (next.examDate || sp.examDate) {
          const raw = next.examDate || sp.examDate || '';
          if (raw.includes('/')) {
            const [d, m, y] = raw.split('/');
            setExamDate(`${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`);
          } else {
            setExamDate(raw.slice(0, 10));
          }
        }
        setTargetSchool(next.targetSchool || sp.targetSchool || '');
      })
      .catch(() => { /* prefill stays as-is */ });
  }, []);

  const handleSave = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/auth');
      return;
    }
    setSaving(true);
    try {
      const body = {
        examName: examName.trim() || null,
        examDate: examDate || null,
        targetSchool: targetSchool.trim() || null,
      };
      const res = await axios.put(`${API}/student/profile`, body, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const updatedUser = res?.data?.user;
      if (updatedUser && updateProgress) {
        updateProgress({ user: updatedUser });
        try { localStorage.setItem('user', JSON.stringify(updatedUser)); } catch { /* ignore */ }
      }
      toast.success('Exam details saved');
      navigate('/welcome');
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Could not save profile';
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const minDate = todayIso();

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-indigo-50/30 to-white dark:from-slate-950 dark:via-indigo-950 dark:to-slate-950">
      <div className="max-w-xl mx-auto px-4 sm:px-6 pt-14 sm:pt-16 pb-12">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/welcome')}
          className="mb-4 text-slate-600 dark:text-slate-300"
          data-testid="profile-back-btn"
        >
          <ArrowLeft className="h-4 w-4 mr-1.5" /> Back to Home
        </Button>

        <Card className="border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900/60" data-testid="student-profile-card">
          <CardHeader>
            <div className="flex items-start gap-3">
              <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-rose-500 to-orange-500 text-white flex items-center justify-center shadow-sm">
                <CalendarDays className="h-5 w-5" />
              </div>
              <div className="min-w-0 flex-1">
                <CardTitle className="text-lg">Your exam target</CardTitle>
                <CardDescription className="mt-0.5">
                  Add the date you&apos;re sitting your next exam so the dashboard can show a personalised countdown.
                </CardDescription>
              </div>
            </div>
          </CardHeader>

          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="exam-name" className="text-sm font-semibold text-slate-700 dark:text-slate-200">
                Exam name
              </Label>
              <Input
                id="exam-name"
                value={examName}
                onChange={(e) => setExamName(e.target.value)}
                placeholder="e.g. Sutton SET Stage 1"
                className="mt-1.5"
                data-testid="profile-exam-name"
              />
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                Optional. Used as the headline on the countdown card.
              </p>
            </div>

            <div>
              <Label htmlFor="exam-date" className="text-sm font-semibold text-slate-700 dark:text-slate-200">
                Exam date
              </Label>
              <Input
                id="exam-date"
                type="date"
                min={minDate}
                value={examDate}
                onChange={(e) => setExamDate(e.target.value)}
                className="mt-1.5"
                data-testid="profile-exam-date"
              />
            </div>

            <div>
              <Label htmlFor="target-school" className="text-sm font-semibold text-slate-700 dark:text-slate-200">
                Target school (optional)
              </Label>
              <Input
                id="target-school"
                value={targetSchool}
                onChange={(e) => setTargetSchool(e.target.value)}
                placeholder="e.g. Sutton Grammar School"
                className="mt-1.5"
                data-testid="profile-target-school"
              />
            </div>

            <div className="pt-2 flex items-center justify-end gap-2">
              <Button
                variant="ghost"
                onClick={() => navigate('/welcome')}
                disabled={saving}
                data-testid="profile-cancel-btn"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                disabled={saving}
                className="bg-violet-600 hover:bg-violet-700"
                data-testid="profile-save-btn"
              >
                {saving ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Saving…</>
                ) : (
                  <><Save className="h-4 w-4 mr-2" /> Save</>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default StudentProfilePage;
